import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Lagerbestand } from '../types';
import { useCustomFields } from '../hooks/useCustomFields';
import './LagerbestandList.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

const LagerbestandList: React.FC = () => {
  const [bestaende, setBestaende] = useState<Lagerbestand[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterVariante, setFilterVariante] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const authToken = localStorage.getItem('auth_token') || '';
  const { definitions: customFields } = useCustomFields('lagerbestand', authToken);

  const loadBestaende = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterVariante) params.append('variante', filterVariante);
      if (filterStatus) params.append('status', filterStatus);
      const url = params.toString()
        ? `${API_URL}/api/lagerbestand?${params}`
        : `${API_URL}/api/lagerbestand`;
      const response = await axios.get(url);
      setBestaende(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Lagerbestände:', error);
    } finally {
      setLoading(false);
    }
  }, [filterVariante, filterStatus]);

  useEffect(() => {
    loadBestaende();
  }, [loadBestaende]);

  const getStatusClass = (status: string) => {
    const map: Record<string, string> = {
      'Verfügbar': 'status-verfuegbar',
      'Reserviert': 'status-reserviert',
      'Gesperrt': 'status-gesperrt',
      'In Prüfung': 'status-pruefung',
    };
    return map[status] || '';
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE');
  };

  return (
    <div className="lager-list-container">
      <div className="list-header">
        <h2>Lagerbestand</h2>
        <div className="header-actions">
          <input
            type="text"
            value={filterVariante}
            onChange={(e) => setFilterVariante(e.target.value)}
            className="filter-select"
            placeholder="Variante filtern..."
          />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="">Alle Status</option>
            <option value="VERFUEGBAR">Verfügbar</option>
            <option value="RESERVIERT">Reserviert</option>
            <option value="GESPERRT">Gesperrt</option>
            <option value="IN_PRUEFUNG">In Prüfung</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading">Lädt...</div>
      ) : bestaende.length === 0 ? (
        <div className="empty-state">
          <p>Keine Lagerbestände vorhanden</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="lager-table">
            <thead>
              <tr>
                <th>Artikel</th>
                <th>Variante</th>
                <th>Lagerplatz</th>
                <th>Charge</th>
                <th>Menge</th>
                <th>Status</th>
                <th>Eingelagert</th>
                {customFields.map(cf => (
                  <th key={cf.feld_id} title={cf.beschreibung || ''}>{cf.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bestaende.map((bestand) => (
                <tr key={bestand.bestand_id}>
                  <td className="artikel-name">{bestand.artikel_bezeichnung || '-'}</td>
                  <td>{bestand.artikel_variante || '-'}</td>
                  <td><span className="lagerplatz-badge">{bestand.lagerplatz}</span></td>
                  <td>{bestand.charge_nr || '-'}</td>
                  <td className="menge-cell">
                    {bestand.menge} {bestand.artikel_einheit || ''}
                  </td>
                  <td>
                    <span className={`status-badge ${getStatusClass(bestand.status)}`}>
                      {bestand.status}
                    </span>
                  </td>
                  <td>{formatDate(bestand.einlagerungsdatum)}</td>
                  {customFields.map(cf => {
                    const val = (bestand.custom_attributes || {})[cf.schluessel];
                    let display: string = '-';
                    if (val !== null && val !== undefined && val !== '') {
                      display = cf.typ === 'BOOLEAN' ? (val ? '✓' : '–') : String(val);
                    }
                    return <td key={cf.feld_id}>{display}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default LagerbestandList;
